export type BrDocumentType = 'cpf' | 'cnpj';

const CPF_NON_DIGITS = /\D/g;
const CNPJ_NON_BODY = /[^0-9A-Z]/gi;

export function stripDocumentDigits(value: string): string {
  return value.replace(CPF_NON_DIGITS, '');
}

export function stripCnpj(value: string): string {
  return value.toUpperCase().replace(CNPJ_NON_BODY, '');
}

function allSameChars(value: string): boolean {
  return value.length > 0 && /^(.)\1+$/.test(value);
}

function cnpjCharValue(char: string): number {
  return char.charCodeAt(0) - 48;
}

function calcCpfCheckDigit(digits: string, factor: number): number {
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    sum += Number(digits[i]) * (factor - i);
  }
  const mod = (sum * 10) % 11;
  return mod === 10 ? 0 : mod;
}

export function isValidCpf(value: string): boolean {
  const digits = stripDocumentDigits(value);
  if (digits.length !== 11 || allSameChars(digits)) return false;
  const d1 = calcCpfCheckDigit(digits.slice(0, 9), 10);
  const d2 = calcCpfCheckDigit(digits.slice(0, 10), 11);
  return digits[9] === String(d1) && digits[10] === String(d2);
}

export function isValidCnpj(value: string): boolean {
  const body = stripCnpj(value);
  if (body.length !== 14 || allSameChars(body)) return false;

  const base = body.slice(0, 12);
  const dv = body.slice(12);
  if (!/^[0-9A-Z]{12}$/.test(base)) return false;
  if (!/^\d{2}$/.test(dv)) return false;

  const calcCheckDigit = (chars: string): string => {
    let size = chars.length;
    let sum = 0;
    let pos = size - 7;
    for (let i = size; i >= 1; i -= 1) {
      sum += cnpjCharValue(chars.charAt(size - i)) * pos;
      pos -= 1;
      if (pos < 2) pos = 9;
    }
    const mod = sum % 11;
    const digit = mod < 2 ? 0 : 11 - mod;
    return String(digit);
  };

  const d1 = calcCheckDigit(base);
  const d2 = calcCheckDigit(base + d1);
  return dv[0] === d1 && dv[1] === d2;
}

export function validateBrDocument(type: BrDocumentType, value: string): boolean {
  return type === 'cpf' ? isValidCpf(value) : isValidCnpj(value);
}
